import { motion } from "framer-motion";
import { Star, Leaf, HeartPulse, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";

const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

// Animation presets hoisted out of render so they keep a stable reference (perf).
const EASE = [0.22, 1, 0.36, 1];
const FADE_UP = { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, ease: EASE } };
const FADE_SCALE = { initial: { opacity: 0, scale: 0.96 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.8, ease: EASE, delay: 0.1 } };
const FADE_BADGE = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { delay: 0.5, duration: 0.6 } };

export const Hero = () => {
  return (
    <section id="hero" className="relative pt-28 lg:pt-36 pb-20 grain-bg overflow-hidden">
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#EDE6D8] blur-3xl opacity-60" />
      <div className="absolute top-1/2 -left-32 w-80 h-80 rounded-full bg-[#F0E4DA] blur-3xl opacity-50" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        <motion.div
          {...FADE_UP}
          className="lg:col-span-6"
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-olive bg-[#F3F7F1] border border-[#E1EADD] px-4 py-2 rounded-full">
            <Leaf size={14} /> Zero Sugar · Diabetic-Friendly
          </span>

          <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] text-ink">
            Guilt-Free
            <span className="block text-terracotta italic">Indulgence</span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-[#524B46] max-w-xl leading-relaxed">
            Handcrafted breads and cookies made with{" "}
            <strong className="text-ink font-semibold">absolutely no added sugar</strong> — sweetened only with maple
            syrup, honey & banana. Treats that taste like the real thing, without spiking your glucose.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4">
            <Button
              data-testid="hero-view-menu"
              onClick={() => go("#menu")}
              className="rounded-full bg-olive hover:bg-olive-hover text-cream h-14 px-8 text-base group"
            >
              View Today's Menu
              <ArrowRight size={18} className="ml-1 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              data-testid="hero-book-cake"
              onClick={() => go("#custom-order")}
              variant="outline"
              className="rounded-full border-2 border-[#D2C7B8] bg-transparent hover:bg-white text-ink h-14 px-8 text-base"
            >
              Order Now
            </Button>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={`hero-star-${i}`} size={16} className="fill-terracotta text-terracotta" />
                ))}
              </div>
              <span className="text-sm font-semibold text-ink">4.9/5</span>
              <span className="text-sm text-[#787069]">· 200+ happy customers</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          {...FADE_SCALE}
          className="lg:col-span-6 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-[0_40px_80px_-40px_rgba(45,40,37,0.45)] aspect-[4/5]">
            <img
              src="/products/hero-feature.jpg"
              alt="Sugar-free almond and chocolate chip cookies styled on a warm cream table"
              className="w-full h-full object-cover"
            />
          </div>

          <motion.div
            {...FADE_BADGE}
            className="absolute -bottom-6 -left-4 sm:left-6 bg-white rounded-2xl shadow-xl border border-[#EFE9E0] p-4 flex items-center gap-3 max-w-[240px]"
          >
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#F3F7F1] text-olive shrink-0">
              <HeartPulse size={22} />
            </span>
            <div>
              <p className="text-sm font-semibold text-ink leading-tight">No glucose spikes</p>
              <p className="text-xs text-[#787069] leading-tight mt-0.5">Verified by our monitoring customers</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
